/**
 * Tip-güvenli i18n yardımcısı — tek dil (Türkçe) için optimize edilmiş.
 *
 * Kullanım:
 *   t("setup.queryLabel")
 *   t("transcript.turnsCount", { n: 7 })
 *
 * Eksik anahtar derleme zamanında yakalanır (TypeScript hatası);
 * runtime'da geri dönüş olarak ham anahtar döner ve console.warn ile uyarılır.
 */
import { tr, type Dictionary } from "./tr";

type Primitive = string | number;

/* ------------------------------------------------------------------
 * Recursive path enumeration over the dictionary type. Yields all
 * dotted leaf paths (skipping object branches).
 * ------------------------------------------------------------------ */
type Join<K, P> = K extends string
  ? P extends string
    ? `${K}.${P}`
    : never
  : never;

type Paths<T, D extends number = 6> = [D] extends [never]
  ? never
  : T extends object
    ? {
        [K in keyof T]-?: T[K] extends string
          ? K & string
          : T[K] extends object
            ? K & string | Join<K, Paths<T[K], Prev[D]>>
            : never;
      }[keyof T]
    : never;

type Prev = [never, 0, 1, 2, 3, 4, 5, 6];

export type TranslationKey = Paths<Dictionary>;

/** Walk dotted path; throws if branch missing (compile-time guarded). */
function lookup(dict: unknown, path: string): unknown {
  const segs = path.split(".");
  let cur: unknown = dict;
  for (const s of segs) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[s];
  }
  return cur;
}

function interpolate(template: string, vars: Record<string, Primitive>): string {
  return template.replace(/\{(\w+)\}/g, (_m, name: string) => {
    const v = vars[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

/**
 * Resolve a typed translation key to its localised string.
 * Optional `vars` map performs `{name}` interpolation.
 */
export function t(
  key: TranslationKey,
  vars?: Record<string, Primitive>,
): string {
  const raw = lookup(tr, key);
  if (typeof raw !== "string") {
    if (typeof console !== "undefined") {
      console.warn(`[i18n] missing string for key "${key}"`);
    }
    return key;
  }
  return vars ? interpolate(raw, vars) : raw;
}

export { tr };
